<template>
  <div class="sessions-list-page">
    <section class="sl-hero">
      <div class="sl-hero__glow" />
      <div class="d-flex align-items-start justify-content-between flex-wrap gap-12">
        <div class="d-flex align-items-center">
          <div class="sl-hero__icon mr-1">
            <feather-icon
              icon="CalendarIcon"
              size="24"
            />
          </div>
          <div>
            <h2 class="mb-0">
              Training Sessions
            </h2>
            <p class="mb-0 sl-hero__subtitle">
              Schedule sessions, manage venues, and track attendance.
            </p>
          </div>
        </div>
        <div class="sl-hero__actions">
          <button
            type="button"
            class="sl-btn sl-btn--refresh"
            :disabled="dataLoad"
            @click="getAllData(currentPage)"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="16"
              :class="{ 'sl-spin-icon': dataLoad }"
            />
            Refresh
          </button>
          <button
            type="button"
            class="sl-btn sl-btn--add"
            @click="$router.push({ name: addComponentName })"
          >
            <feather-icon
              icon="PlusIcon"
              size="16"
            />
            {{ addType }}
          </button>
        </div>
      </div>

      <div class="sl-stats">
        <div class="sl-stat">
          <div class="sl-stat__icon sl-stat__icon--primary">
            <feather-icon
              icon="VideoIcon"
              size="18"
            />
          </div>
          <div>
            <div class="sl-stat__value">
              {{ totalRows }}
            </div>
            <div class="sl-stat__label">
              Total sessions
            </div>
          </div>
        </div>
        <div class="sl-stat">
          <div class="sl-stat__icon sl-stat__icon--info">
            <feather-icon
              icon="HomeIcon"
              size="18"
            />
          </div>
          <div>
            <div class="sl-stat__value">
              {{ internalCount }}
            </div>
            <div class="sl-stat__label">
              Internal on page
            </div>
          </div>
        </div>
        <div class="sl-stat">
          <div class="sl-stat__icon sl-stat__icon--success">
            <feather-icon
              icon="UsersIcon"
              size="18"
            />
          </div>
          <div>
            <div class="sl-stat__value">
              {{ pageAttendeesCount }}
            </div>
            <div class="sl-stat__label">
              Attendees on page
            </div>
          </div>
        </div>
      </div>
    </section>

    <div class="sl-toolbar">
      <div class="sl-search">
        <feather-icon
          icon="SearchIcon"
          size="15"
          class="sl-search__icon"
        />
        <input
          v-model="searchQuery"
          type="text"
          class="sl-search__input"
          :class="{ 'sl-search__input--has-clear': searchQuery }"
          placeholder="Search sessions by training, trainer, venue… (press Enter)"
          @keyup.enter="onSearch"
        >
        <button
          v-if="searchQuery"
          type="button"
          class="sl-search__clear"
          title="Clear search"
          @click="clearSearch"
        >
          <feather-icon
            icon="XIcon"
            size="14"
          />
        </button>
      </div>
    </div>

    <div
      v-if="dataLoad"
      class="sl-loading"
    >
      <div class="sl-loading__spinner" />
      <p class="mb-0 text-muted">
        Loading training sessions…
      </p>
    </div>

    <div
      v-else-if="!sessions.length"
      class="sl-empty"
    >
      <div class="sl-empty__icon">
        <feather-icon
          icon="InboxIcon"
          size="30"
        />
      </div>
      <h5 class="mb-50">
        No training sessions found
      </h5>
      <p class="mb-0 text-muted">
        {{ searchQuery ? 'Try adjusting your search.' : 'Create a new session to get started.' }}
      </p>
    </div>

    <div
      v-else
      class="sl-grid"
    >
      <article
        v-for="session in sessions"
        :key="session.id"
        class="sl-card"
      >
        <div class="sl-card__accent" />
        <header class="sl-card__header">
          <div class="sl-card__icon">
            <feather-icon
              icon="BookOpenIcon"
              size="20"
            />
          </div>
          <div class="sl-card__title-wrap">
            <h3
              class="sl-card__title"
              :title="session.training ? session.training.name : ''"
            >
              {{ session.training ? session.training.name : '—' }}
            </h3>
            <b-badge
              pill
              :variant="session.is_internal ? 'light-info' : 'light-secondary'"
            >
              <feather-icon
                :icon="session.is_internal ? 'HomeIcon' : 'GlobeIcon'"
                size="12"
                class="mr-25"
              />
              {{ session.is_internal ? 'Internal' : 'External' }}
            </b-badge>
          </div>
        </header>

        <div class="sl-card__details">
          <div class="sl-detail">
            <feather-icon
              icon="ClockIcon"
              size="14"
            />
            <div>
              <span class="sl-detail__label">From</span>
              <span class="sl-detail__value">{{ session.from || '—' }}</span>
            </div>
          </div>
          <div class="sl-detail">
            <feather-icon
              icon="ClockIcon"
              size="14"
            />
            <div>
              <span class="sl-detail__label">To</span>
              <span class="sl-detail__value">{{ session.to || '—' }}</span>
            </div>
          </div>
          <div class="sl-detail">
            <feather-icon
              icon="MapPinIcon"
              size="14"
            />
            <div>
              <span class="sl-detail__label">Venue</span>
              <span class="sl-detail__value">{{ session.training_venue || '—' }}</span>
            </div>
          </div>
          <div class="sl-detail">
            <feather-icon
              icon="UserIcon"
              size="14"
            />
            <div>
              <span class="sl-detail__label">Trainer</span>
              <span class="sl-detail__value">{{ session.trainer || '—' }}</span>
            </div>
          </div>
        </div>

        <div class="sl-card__attendance">
          <button
            type="button"
            class="sl-attendance-btn"
            title="Open engineers list and mark attendance"
            @click="showEngineersList(session.id)"
          >
            <span class="sl-attendance-btn__icon">
              <feather-icon
                icon="UserCheckIcon"
                size="20"
              />
            </span>
            <span class="sl-attendance-btn__content">
              <span class="sl-attendance-btn__label">
                Manage Attendance
              </span>
              <span class="sl-attendance-btn__hint">
                Tap to view engineers & mark who attended
              </span>
            </span>
            <span class="sl-attendance-btn__meta">
              <span class="sl-attendance-btn__count">{{ session.attendees_count || 0 }}</span>
              <feather-icon
                icon="ChevronRightIcon"
                size="18"
                class="sl-attendance-btn__arrow"
              />
            </span>
          </button>
        </div>

        <div class="sl-card__actions">
          <a
            :href="editLink(session)"
            class="sl-action sl-action--edit"
            title="Edit session"
            target="_blank"
            rel="noopener"
          >
            <feather-icon
              icon="EditIcon"
              size="15"
            />
            Edit
          </a>
          <button
            type="button"
            class="sl-action sl-action--delete"
            title="Delete session"
            :disabled="deleteLoadingId === session.id"
            @click="deleteSession(session.id)"
          >
            <feather-icon
              icon="Trash2Icon"
              size="15"
            />
            Delete
          </button>
        </div>
      </article>
    </div>

    <div
      v-if="!dataLoad && totalRows > perPage"
      class="sl-pagination"
    >
      <div class="sl-pagination__info">
        Showing
        <span class="font-weight-bold">{{ rangeStart }}</span>
        -
        <span class="font-weight-bold">{{ rangeEnd }}</span>
        of
        <span class="font-weight-bold">{{ totalRows }}</span>
      </div>
      <b-pagination
        v-model="currentPage"
        :total-rows="totalRows"
        :per-page="perPage"
        align="center"
        first-number
        last-number
        @change="onPageChange"
      />
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { isString } from 'lodash'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import decryptData from '@/store/auth/decryptData'

export default {
  name: 'SessionsTraining',
  data() {
    return {
      apiUrl: 'training-session',
      addType: 'Add Session',
      addComponentName: 'add-sessions-training',
      editComponent: 'edit-sessions-training',
      dataLoad: false,
      deleteLoadingId: null,
      sessions: [],
      perPage: 20,
      currentPage: 1,
      totalRows: 0,
      searchQuery: '',
    }
  },
  computed: {
    internalCount() {
      return this.sessions.filter(session => session.is_internal).length
    },
    pageAttendeesCount() {
      return this.sessions.reduce((sum, session) => sum + (session.attendees_count || 0), 0)
    },
    rangeStart() {
      if (!this.totalRows) return 0
      return (this.currentPage - 1) * this.perPage + 1
    },
    rangeEnd() {
      return Math.min(this.currentPage * this.perPage, this.totalRows)
    },
  },
  mounted() {
    this.getAllData(1)
  },
  methods: {
    onSearch() {
      this.getAllData(1)
    },
    clearSearch() {
      this.searchQuery = ''
      this.getAllData(1)
    },
    onPageChange(page) {
      this.getAllData(page)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    editLink(session) {
      return this.$router.resolve({
        name: this.editComponent,
        params: {
          id: session.id,
          name: session.training ? session.training.name : '',
        },
      }).href
    },
    async getAllData(page = 1) {
      this.currentPage = page
      this.dataLoad = true
      try {
        const params = {
          skip: (page - 1) * this.perPage,
        }
        const search = this.searchQuery.trim()
        if (search) {
          params.search = search
        }
        const response = await axios.get(this.apiUrl, { params })
        if (response.status === 200) {
          const { data } = decryptData(response.data.payload)
          const listKey = Object.keys(data || {}).find(key => key !== 'count')
          const list = listKey ? data[listKey] : []
          this.sessions = Array.isArray(list) ? list : []
          this.totalRows = data?.count ?? this.sessions.length
        }
      } catch (e) {
        this.showError(e, 'Failed to load training sessions')
      } finally {
        this.dataLoad = false
      }
    },
    deleteSession(id) {
      this.$swal({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!',
      }).then(result => {
        if (result.isConfirmed) {
          this.deleteLoadingId = id
          axios.delete(`${this.apiUrl}/${id}`)
            .then(() => {
              this.$swal('Deleted!', 'Deleted Successfully.', 'success')
              this.getAllData(this.currentPage)
            })
            .catch(error => {
              const payload = decryptData(error.response.data.payload).message
              let errorMessage = 'Failed to delete session'
              if (isString(payload)) {
                errorMessage = payload
              } else if (payload && typeof payload === 'object') {
                const firstArrayKey = Object.keys(payload).find(key => Array.isArray(payload[key]))
                errorMessage = firstArrayKey ? payload[firstArrayKey][0] : errorMessage
              }
              this.showError(null, errorMessage)
            })
            .finally(() => {
              this.deleteLoadingId = null
            })
        }
      })
    },
    ApprovalRow(name) {
      this.$swal({
        title: 'Engineer List',
        html: this.formatEngineerNames(name),
        showCancelButton: false,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Close',
        didOpen: () => {
          const checkboxes = this.$swal.getHtmlContainer().querySelectorAll('input[type="checkbox"]')

          const setAllDisabled = disabled => {
            checkboxes.forEach(cb => {
              // eslint-disable-next-line no-param-reassign
              cb.disabled = disabled
            })
          }

          checkboxes.forEach(originalCheckbox => {
            originalCheckbox.addEventListener('change', () => {
              const checkbox = originalCheckbox
              const engineerId = checkbox.getAttribute('data-id')
              const attended = checkbox.checked

              setAllDisabled(true)
              this.$swal.showLoading()

              axios.put(`training-session-attendee/${engineerId}`, {
                is_attended: attended,
              })
                .then(() => {
                  this.$toast.success('Attendance updated!')
                })
                .catch(() => {
                  checkbox.checked = !attended
                  this.$toast.error('Failed to update attendance.')
                })
                .finally(() => {
                  setAllDisabled(false)
                  this.$swal.hideLoading()
                })
            })
          })
        },
      })
    },
    formatEngineerNames(names) {
      return `
        <table style="border: 0.5px solid; border-collapse: collapse; margin-top: 18px !important;  margin: auto;">
          <tr style="background-color: #f3f2f7">
            <th style="border: 0.5px solid; padding: 8px; text-align: left;">#</th>
            <th style="border: 0.5px solid; padding: 8px; text-align: left;">Engineer Names</th>
            <th style="border: 0.5px solid; padding: 8px; text-align: left;">Attended</th>
          </tr>
          ${names.map((name, index) => `
            <tr>
              <td style="border: 1px solid; padding: 8px;">${index + 1}</td>
              <td style="border: 1px solid; padding: 8px; text-align: left !important; text-wrap: wrap !important;">${name.name}</td>
              <td style="border: 1px solid; padding: 8px; text-align: center !important; text-wrap: wrap !important;">
                 <input type="checkbox" data-id="${name.id}" ${name.attended ? 'checked' : ''}>
                 </td>
            </tr>`).join('')}
        </table>
      `
    },
    showEngineersList(id) {
      this.$swal({
        title: 'Loading...',
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          this.$swal.showLoading()
        },
      })

      axios
        .get(`training-session/${id}`)
        .then(res => {
          const engineersList = decryptData(res.data.payload).data.trainingSession.attendees.map(item => ({
            name: item.attendee_name,
            id: item.id,
            attended: item.is_attended,
          }))
          this.$swal.close()
          this.ApprovalRow(engineersList)
        }).catch(error => {
          if (error) {
            this.$swal.close()
            this.$swal({
              icon: 'error',
              title: 'Failed to load engineers',
              text: 'Please try again later.',
            })
          }
        })
    },
    showError(error, fallback) {
      let message = fallback
      try {
        if (error?.response?.data?.payload) {
          const payload = decryptData(error.response.data.payload)
          const msg = payload?.message || payload
          if (typeof msg === 'string') message = msg
        }
      } catch {
        // keep fallback
      }
      this.$toast({
        component: ToastificationContent,
        props: {
          title: message,
          icon: 'BellIcon',
          variant: 'danger',
        },
      })
    },
  },
}
</script>

<style lang="scss" src="./sessions-training.scss"></style>
